import { extractOrdinalOutpointFromUrl } from 'components/shared/ordinalUrlUtils'

export type OrdinalControlsMode = 'legacy' | 'caption'

// Central rollout policy for BSV ordinal controls. Keep UI callers isolated
// from the rollout mechanism so this can be toggled here.
export function bsvOrdinalControlsEnabled(): boolean {
    return true
}

export function getOrdinalControlsMode(): OrdinalControlsMode {
    return bsvOrdinalControlsEnabled() ? 'caption' : 'legacy'
}

function isEnabledOrdinalWrapperThread(
    messageType: string | null | undefined,
    isSystem: boolean | null | undefined,
): boolean {
    return messageType === 'ordinal_wrapper' && isSystem === true && bsvOrdinalControlsEnabled()
}

type UserId = string | number

export function resolveUpvalueOwnerId(
    answerAuthorId: UserId | null | undefined,
    explicitArtifactOwnerId: UserId | null | undefined,
    wrappedArtifactOwnerId: UserId | null | undefined,
): UserId | null | undefined {
    return explicitArtifactOwnerId || wrappedArtifactOwnerId || answerAuthorId
}

type QuestReplyAreaParams = {
    isDraft: boolean
    isSystem: boolean
    leaderboardAnnouncement: boolean
    marketCard?: boolean
    /** A Treechat Radio play room: system-authored, but a real public room. */
    radioPlay?: boolean
    mediaAnnouncement?: boolean
    /** A direct-message conversation: system-owned so neither side owns it. */
    directMessage?: boolean
    messageType: string | null | undefined
}

function isEnabledSystemDiscussionThread({
    isSystem,
    marketCard,
    radioPlay,
    mediaAnnouncement,
    directMessage,
    messageType,
}: Pick<
    QuestReplyAreaParams,
    'isSystem' | 'marketCard' | 'radioPlay' | 'mediaAnnouncement' | 'directMessage' | 'messageType'
>): boolean {
    return (
        isEnabledOrdinalWrapperThread(messageType, isSystem) ||
        (isSystem === true && marketCard === true) ||
        // Radio play rooms exist to be talked in: the station authors the head
        // card, listeners chat under it. A direct message is the same shape:
        // a system head the two participants talk under.
        (isSystem === true &&
            (radioPlay === true || mediaAnnouncement === true || directMessage === true))
    )
}

export function shouldShowQuestReplyArea({
    isDraft,
    isSystem,
    leaderboardAnnouncement,
    marketCard,
    radioPlay,
    mediaAnnouncement,
    directMessage,
    messageType,
}: QuestReplyAreaParams): boolean {
    if (isDraft) return false
    if (!isSystem || leaderboardAnnouncement) return true

    return isEnabledSystemDiscussionThread({
        isSystem,
        marketCard,
        radioPlay,
        mediaAnnouncement,
        directMessage,
        messageType,
    })
}

type QuestTitleNavigationParams = QuestReplyAreaParams & {
    isEditing: boolean
}

export function shouldNavigateQuestTitle({
    isDraft,
    isEditing,
    isSystem,
    leaderboardAnnouncement,
    marketCard,
    radioPlay,
    mediaAnnouncement,
    directMessage,
    messageType,
}: QuestTitleNavigationParams): boolean {
    if (isDraft || isEditing) return false
    if (!isSystem || leaderboardAnnouncement) return true

    return isEnabledSystemDiscussionThread({
        isSystem,
        marketCard,
        radioPlay,
        mediaAnnouncement,
        directMessage,
        messageType,
    })
}

export function shouldRenderOrdinalWrapperChrome(
    messageType: string | null | undefined,
    isSystem: boolean | null | undefined,
    contentCandidates: Array<string | null | undefined>,
): boolean {
    if (!isEnabledOrdinalWrapperThread(messageType, isSystem)) return false

    return contentCandidates.some(candidate =>
        candidate ? !!extractOrdinalOutpointFromUrl(String(candidate)) : false,
    )
}

export function shouldShowOrdinalOwnership(
    hasCurrentOwner: boolean,
    ownerCount: number | null | undefined,
): boolean {
    return hasCurrentOwner || (ownerCount || 0) > 0
}
